import { Component, input, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { S25TableModule } from "../../../s25-table/s25.table.module";
import { Table } from "../../../s25-table/Table";
import { S25Util } from "../../../../util/s25-util";
import { Bind } from "../../../../decorators/bind.decorator";
import { BudgetService } from "../budget.service";
import { BudgetCreateModalComponent } from "./budget.create.modal.component";
import { CacheRepository } from "../../../../decorators/cache.decorator";
import { UserprefService } from "../../../../services/userpref.service";
import { S25Datefilter } from "../../../s25-dateformat/s25.datefilter.service";

@TypeManagerDecorator("s25-ng-budget-admin-organizations")
@Component({
    selector: "s25-ng-budget-admin-organizations",
    template: `
        <div class="organizations-tab">
            <div class="section-header">
                <h3>Organization Budgets - {{ selectedFiscalYear() }}</h3>
                <s25-ng-budget-create-modal></s25-ng-budget-create-modal>
            </div>

            @if (errorMsg()) {
                <div class="alert alert-danger" role="alert">
                    {{ errorMsg() }}
                </div>
            }
            <s25-ng-table
                [dataSource]="organizationTableData"
                [hasRefresh]="true"
                [hasFilter]="true"
                [columnSortable]="true"
                [hasTotalRowCount]="true"
                [caption]="'Organization Budget Status'"
                [noResultsMessage]="'No organizations found'"
            ></s25-ng-table>
        </div>
    `,
    styles: `
        .organizations-tab {
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            overflow: hidden;
        }

        .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1.5rem;
            background: #f8f9fa;
            border-bottom: 1px solid #e5e5e5;
        }

        .section-header h3 {
            margin: 0;
            font-size: 1.2rem;
            color: #333;
            font-weight: 600;
        }

        @media (max-width: 768px) {
            .section-header {
                flex-direction: column;
                gap: 1rem;
                align-items: stretch;
            }

            .section-header s25-ng-button {
                width: 100%;
            }
        }
    `,
    standalone: true,
    imports: [S25TableModule, BudgetCreateModalComponent],
})
export class BudgetAdminOrganizationsComponent {
    selectedFiscalYear = input.required<string>();

    errorMsg = signal("");

    organizationTableData: Table.DataSource = {
        type: "unpaginated",
        dataSource: this.getOrganizationTableRows,
        columns: [
            {
                id: "organizationName",
                header: "Organization",
                minWidth: 200,
                cells: {
                    text: "",
                    className: "font-weight-medium",
                },
            },
            {
                id: "organizationType",
                header: "Type",
                minWidth: 120,
                cells: {
                    text: "",
                },
            },
            {
                id: "budgetStatus",
                header: "Budget Status",
                minWidth: 120,
                align: "center",
                cells: {
                    text: "",
                    className: "budget-status",
                },
            },
            {
                id: "allocatedAmount",
                header: "Allocated Amount",
                minWidth: 150,
                align: "right",
                cells: {
                    text: "",
                    className: "currency-amount",
                },
            },
            {
                id: "utilizationPercentage",
                header: "Utilization",
                minWidth: 100,
                align: "center",
                cells: {
                    text: "",
                },
            },
            {
                id: "lastModified",
                header: "Last Modified",
                minWidth: 120,
                cells: {
                    text: "",
                },
            },
        ],
    };

    @Bind
    async getOrganizationTableRows(): Promise<Table.DataSourceResponse> {
        this.errorMsg.set("");
        const dateFormat = await UserprefService.getS25Dateformat();
        CacheRepository.invalidateByService("BudgetService", "getOrganizationBudgetStatus");
        const [budgets, error] = await S25Util.Maybe(
            BudgetService.getOrganizationBudgetStatus(this.selectedFiscalYear()),
        );

        if (error) {
            this.errorMsg.set(`${S25Util.errorText(error, "Failed to load organization budgets.")}`);
            return { rows: [], totalRows: 0 };
        }

        const rows: Table.Row[] = budgets.map((budget) => ({
            id: budget.organizationId,
            name: budget.organizationName,
            cells: {
                organizationName: { text: budget.organizationName },
                organizationType: { text: budget.organizationType },
                budgetStatus: { text: budget.budgetStatus },
                allocatedAmount: { text: budget.allocatedAmount },
                utilizationPercentage: { text: budget.utilizationPercentage },
                lastModified: { text: S25Datefilter.transform(budget.lastModDate, dateFormat) },
            },
        }));
        return { rows, totalRows: budgets.length };
    }
}
